import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AuthenticationResult, type EventMessage } from '@azure/msal-browser';
import { apiScopes, msalInstance } from './auth/msalConfig';
import { claimAutoSignIn, linkAccount, rememberSignedIn, signedOutOnPurpose } from './auth/ssoMemory';
import { addressWithoutLoginHint, isAuthResponse, linkLoginHint } from './auth/linkHint';
import { App } from './App';

// Design system bundle — tokens + fonts + reset, applied to the document
// in one import. Brand expression then resolves at runtime via the
// data-brand attribute set on <html> by @wise/ui's WiseThemeProvider (App.tsx).
import '@wise/design-system/styles';

// Responsive utility classes (mobile reflow) — after the design system so
// these media-scoped overrides layer on top of the baseline.
import './index.css';

// Read before MSAL touches the address: the page carrying Entra's answer, and
// the hidden renewal iframe (which loads this same page), never start a
// sign-in of their own.
const onAuthResponse = isAuthResponse(window.location.search, window.location.hash);
const inHiddenFrame = window.self !== window.top;

// MSAL v3+ requires explicit initialize() before any auth call. We must
// also call handleRedirectPromise() on first load to complete the
// loginRedirect / acquireTokenRedirect round-trip from the auth provider.
async function bootstrap() {
  await msalInstance.initialize();

  const render = (): void => {
    const rootEl = document.getElementById('root');
    if (!rootEl) throw new Error('No #root element');
    createRoot(rootEl).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </StrictMode>,
    );
  };

  // EVERY LINK LANDS SIGNED IN (the platform rule, Chauncey White 2026-10-23
  // — auth/ssoMemory.ts). Remember who signed in: the hint for the next link
  // that carries none. Whether they signed out ON PURPOSE is the menu's to say
  // (App.tsx), never LOGOUT_START. Registered BEFORE handleRedirectPromise:
  // the redirect's LOGIN_SUCCESS fires inside it.
  // ⚠ msal-browser 4.x (installed 4.30.0 — StandardController emits
  // LOGIN_SUCCESS with the AuthenticationResult): the account is
  // `payload.account`. On 5.x LOGIN_SUCCESS carries the AccountInfo ITSELF;
  // re-check this on an upgrade (portal/tests/sso-memory.test.ts pins it).
  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      const account = (event.payload as AuthenticationResult | null)?.account ?? null;
      rememberSignedIn(account?.username);
      // Whoever just signed in is who this tab is — not the first cached.
      if (account) msalInstance.setActiveAccount(account);
    } else if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
      rememberSignedIn((event.payload as AuthenticationResult | null)?.account?.username);
    }
  });

  const redirectResult = await msalInstance.handleRedirectPromise();

  // The cache is shared across tabs now: a tab opened from a link uses the
  // account already there — the one the link NAMES (auth/linkHint.ts). A link
  // for somebody the browser does not hold signs them in instead of opening
  // as whoever is cached (on a shared PC, the last person who did not sign
  // out) — once per tab, never after a chosen sign-out, never on the page
  // carrying Entra's answer or the load that just came back from it, never in
  // the hidden renewal iframe.
  const linked = linkAccount(msalInstance.getAllAccounts(), linkLoginHint(window.location.search, window.location.hash));
  if (linked.account !== null) msalInstance.setActiveAccount(linked.account);
  else if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Cached accounts but no active one: pick the first so useMsal returns it.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]!);
  }
  if (
    linked.signInAs !== null &&
    redirectResult === null &&
    !onAuthResponse &&
    !inHiddenFrame &&
    !signedOutOnPurpose() &&
    claimAutoSignIn()
  ) {
    void msalInstance.loginRedirect({ scopes: apiScopes, loginHint: linked.signInAs }).catch((err: unknown) => {
      // The redirect itself failed (not Entra refusing): open as whoever is
      // cached rather than a blank page — SignInGate never runs here.
      console.warn("[itms-qc] sign-in for the link's person failed:", err);
      render();
    });
    return;
  }

  // Signed in: the link's ?login_hint has done its work. An address copied
  // from here into Teams must not carry THIS person's hint — the colleague who
  // opens it would be signed in as, or asked the password of, somebody else
  // (auth/linkHint.ts). Stripped BEFORE the router mounts, so react-router
  // starts from the clean address and never writes the hint back; the path,
  // the hash and every other query key stay. Nobody signed in keeps it: the
  // automatic sign-in (App.tsx SignInGate) reads it.
  if (msalInstance.getActiveAccount() !== null && !inHiddenFrame) {
    const url = addressWithoutLoginHint(window.location.pathname, window.location.search, window.location.hash);
    if (url !== null) window.history.replaceState(window.history.state, '', url);
  }

  render();
}

bootstrap().catch((err) => {
  console.error('[main] bootstrap failed:', err);
  const rootEl = document.getElementById('root');
  if (rootEl) {
    rootEl.textContent =
      'Portal failed to initialize. Reload the page; if that doesn\'t help, contact IT.';
  }
});
